// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Terminals" on a machine's page in Settings › Connections › Agents (design/
// agents-terminals s1-s2; JP 2026-10-22): one switch, on by default, that lets its owner open the
// machine's terminals from Konvo, see them live and type into them. Only the owner, never people a
// chat is shared with. Konvo holds the machine to it, and so does the machine's own cap
// (`konvo-agents config --terminals off`), shown here when set.

import {SwitchRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import {shareErrorMessage} from '@app/features/konvo_agents/components/AgentShareBits';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

/** Whether the card shows the row: the machine sends and its konvo-agents has terminals (or capped them). */
export const showsTerminalsSetting = (machine: AgentMachineView) =>
	machine.limits.send &&
	(machine.limits.terminalsCap !== undefined || machine.tools.some((tool) => tool.can.terminalSessions === true));

export const AgentMachineTerminals = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [saving, setSaving] = useState(false);
	const on = machine.limits.terminals !== false;
	const capped = machine.limits.terminalsCap === 'off';
	const name = machine.name;
	const save = async (value: boolean) => {
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, {terminals: value});
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	return (
		<SwitchRow
			icon={<IconTerminal2 size={18} stroke={1.7} />}
			title={t({
				message: 'Terminals',
				comment: 'Agents Settings: switch that lets the owner open and type into the machine’s terminals from Konvo.',
			})}
			description={
				capped
					? t({
							message: `Off on ${name} itself (konvo-agents config --terminals on there)`,
							comment:
								'Agents Settings: the machine’s owner turned terminals off on the machine. The name is the computer.',
						})
					: t({
							message: 'Open and type in terminals here',
							comment:
								'Agents Settings: what the Terminals switch means. Only the owner, never people a chat is shared with.',
						})
			}
			value={on && !capped}
			disabled={saving || capped}
			onChange={(value) => void save(value)}
			data-flx="konvo-agents.machine.terminals"
		/>
	);
});
